import { useCallback, useMemo, useState } from 'react';

import { isGapDraftEnriched, type GapDraftView } from '@momentum/types';

import { useEditGapDraft } from '@/shared/hooks/useGapReview';
import {
  expandEditableFields,
  isEditedPath,
  problemWith,
  seedValueOf,
  toEditChanges,
  type EditableGapFieldState,
} from '../lib/draft-edit';

export interface DraftEditForm {
  /**
   * Whether the draft in front of the reviewer has anything final to edit —
   * enrichment finished cleanly AND left at least one populated field.
   */
  isEditable: boolean;
  isEditing: boolean;
  fields: EditableGapFieldState[];
  /** How many changes a save would send — not how many fields were touched. */
  changeCount: number;
  hasProblem: boolean;
  isSaving: boolean;
  start: () => void;
  cancel: () => void;
  setValue: (path: string, value: string) => void;
  setReason: (path: string, reason: string) => void;
  save: () => void;
}

interface UseDraftEditFormOptions {
  onSaved: (changeCount: number) => void;
  onError: (message: string) => void;
}

/**
 * The reader's edit mode, held by the route.
 *
 * It lives here rather than inside the form component for two reasons: the
 * keyboard hook needs `save` (⌘↵ has to mean save while the form is open), and
 * edit mode has to end when the reviewer walks to the next gap. That second one
 * is why the open draft is tracked by id rather than by a boolean — J moves the
 * reader, and a boolean would leave the next draft's form open holding the
 * previous draft's typing.
 *
 * Walking back to that draft re-opens the form on the typing it still holds,
 * the way an unposted comment survives the same trip. The values are keyed by
 * path alone, which is safe only because they are read while exactly one draft
 * is open — moving off closes the form, so no other draft can see them.
 *
 * Typed values are unsaved work and nothing here writes them anywhere durable:
 * cancelling, or a save, drops them.
 */
export function useDraftEditForm(
  draft: GapDraftView | undefined,
  { onSaved, onError }: UseDraftEditFormOptions
): DraftEditForm {
  const [editingDraftId, setEditingDraftId] = useState<string | undefined>();
  const [valuesByPath, setValuesByPath] = useState<Record<string, string>>({});
  const [reasonsByPath, setReasonsByPath] = useState<Record<string, string>>(
    {}
  );

  // Destructured so `save` can be memoised: `mutate` keeps its identity across
  // renders where the mutation result object does not, and the keyboard hook
  // rebinds a document listener every time `save` changes.
  const { mutate: saveEdits, isPending: isSaving } = useEditGapDraft();

  /**
   * The draft's final fields — what edit mode would offer. Computed whether or
   * not the form is open, because "is there anything to edit" is the button's
   * enabled state: an enriched draft with nothing populated has no form to show,
   * and opening an empty one would read as the feature being broken.
   */
  const editableFields = useMemo(
    () =>
      draft !== undefined && isGapDraftEnriched(draft.agents)
        ? expandEditableFields(draft)
        : [],
    [draft]
  );

  const isEditable = editableFields.length > 0;
  const isEditing = draft !== undefined && editingDraftId === draft._id;

  const fields = useMemo((): EditableGapFieldState[] => {
    if (draft === undefined || !isEditing) return [];

    return editableFields.map((editableField) => {
      const seedValue = seedValueOf(draft, editableField.path);
      const value = valuesByPath[editableField.path] ?? seedValue;
      return {
        ...editableField,
        value,
        seedValue,
        reason: reasonsByPath[editableField.path] ?? '',
        isDirty: value !== seedValue,
        wasEdited: isEditedPath(draft, editableField.path),
        problem: problemWith(editableField, value),
      };
    });
  }, [draft, editableFields, isEditing, valuesByPath, reasonsByPath]);

  const changes = useMemo(() => toEditChanges(fields), [fields]);

  const start = useCallback(() => {
    if (draft === undefined || !isEditable) return;
    setValuesByPath({});
    setReasonsByPath({});
    setEditingDraftId(draft._id);
  }, [draft, isEditable]);

  const cancel = useCallback(() => {
    setEditingDraftId(undefined);
    setValuesByPath({});
    setReasonsByPath({});
  }, []);

  const setValue = useCallback((path: string, value: string) => {
    setValuesByPath((values) => ({ ...values, [path]: value }));
  }, []);

  const setReason = useCallback((path: string, reason: string) => {
    setReasonsByPath((reasons) => ({ ...reasons, [path]: reason }));
  }, []);

  const hasProblem = fields.some((field) => field.problem !== undefined);

  const save = useCallback(() => {
    if (draft === undefined || !isEditing) return;
    if (changes.length === 0 || hasProblem || isSaving) return;

    const changeCount = changes.length;
    saveEdits(
      { draftId: draft._id, changes },
      {
        onSuccess: () => {
          cancel();
          onSaved(changeCount);
        },
        onError: (error) => onError(error.message),
      }
    );
  }, [
    cancel,
    changes,
    draft,
    hasProblem,
    isEditing,
    isSaving,
    onError,
    onSaved,
    saveEdits,
  ]);

  return {
    isEditable,
    isEditing,
    fields,
    changeCount: changes.length,
    hasProblem,
    isSaving,
    start,
    cancel,
    setValue,
    setReason,
    save,
  };
}
